<template>
  <div>
    <IconButton
      v-if="isNewEntry"
      src="bwise/action_add"
      style="transform: scaleX(-1)"
      @click="toggleShow"
      v-tooltip.bottom-end="addNewLabel"
    />
    <span v-if="!isNewEntry" class="text-primary-500 cursor-pointer" @click="toggleShow">{{
      editorData.name
    }}</span>
    <portal to="main-modal">
      <EHSModal
        v-if="show"
        headerTitle="Task"
        primaryButtonText="OK"
        secondaryButtonText="Discard"
        :hasSecondaryAction="true"
        :primary-action-function="onTaskAddClick"
        :secondaryActionFunction="toggleShow"
      >
        <FieldDetacher>
          <Layout column>
            <FormField label="Number">
              <TextField :value.sync="editableData.number" required ref="numberField" />
            </FormField>
            <FormField label="Task">
              <TextField :value.sync="editableData.name" required ref="nameField" />
            </FormField>
            <FormField label="Details">
              <TextEditor v-model="editableData.details" />
            </FormField>
            <FormField name="photos" label="Photos">
              <FileList
                ref="taskFileList"
                v-if="fileListSetupComplete"
                :service="fileService"
                :maxFileSize="maxFileSize"
                :acceptedExtensions="supportedImageExtensions"
                :value.sync="editableData.photos"
              >
                <template #icon="{ file }">
                  <div class="w-file-list__thumbnail" v-if="file.data.id">
                    <img alt="thumbnail" :src="getThumbnailUrl(file)" />
                  </div>
                </template>
              </FileList>
            </FormField>
            <FormField label="Steps">
              <EHSTable
                ref="stepTable"
                :columns="columns"
                :tableRowData="editableData.steps"
                :rowKeyAttrib="'number'"
                :tabThroughAction="addNewEmptyRow"
                :selectable="true"
                :updateAction="() => (this.isDirty = true)"
                :isWholeRowNotEmpty="isWholeRowNotEmpty"
              >
                <template #actions>
                  <IconButton
                    src="bwise/action_add"
                    style="transform: scaleX(-1)"
                    @click="addNewEmptyRow"
                    v-tooltip.bottom-end="addNewLabel"
                /></template>
                <template v-slot:selection-actions="values">
                  <IconButton
                    src="trash"
                    @click="onSelectedStepDeleteClick(values.selectedItems)"
                  ></IconButton>
                </template>
              </EHSTable>
            </FormField>
            <FormField name="allowNA" label="Allow to be marked as N/A">
              <Checkbox :value.sync="editableData.allowNA" :checked="editableData.allowNA" />
            </FormField>
          </Layout>
        </FieldDetacher>
      </EHSModal>
    </portal>
  </div>
</template>

<script>
import Layout from '@wizer/core/components/layout/Layout'
import TextField from '@wizer/core/components/text-field/TextField'
import TextEditor from '@wizer/core/components/text-editor/TextEditor'
import IconButton from '@wizer/core/components/icon-button/IconButton'
import _ from 'lodash'
import Checkbox from '@wizer/core/components/checkbox/Checkbox.vue'
import { mapState } from 'vuex'
import FormField from '@/components/common/FormField'
import FileList from '@/components/common/FileList'
import EHSModal from '@/components/EHSModal'
import FieldDetacher from '@/components/common/FieldDetacher'
import AttachmentMixin from '@/mixins/AttachmentMixin'
import WalkthroughAttachmentService from '@/services/WalkthroughAttachmentService'
import SubFormMixin from '@/mixins/SubFormMixin'
import DataTableMixin from '@/mixins/DataTableMixin'
import EHSTable from '@/components/table/EHSTable'
import PreventCloseMixin from '@/mixins/PreventCloseMixin'

export default {
  name: 'TaskEditor',
  mixins: [AttachmentMixin, SubFormMixin, DataTableMixin, PreventCloseMixin],
  components: {
    EHSTable,
    Layout,
    FormField,
    Checkbox,
    TextField,
    TextEditor,
    EHSModal,
    FieldDetacher,
    IconButton,
    FileList,
  },
  props: {
    taskValidator: Function,
    entryIndex: Number,
    editorData: { type: Object, default: () => ({ steps: [] }) },
  },
  data() {
    return {
      columns: [
        {
          property: 'number',
          label: 'Number',
          width: '1/5',
          unique: true,
          required: true,
          maxLength: 20,
        },
        {
          property: 'step',
          label: 'Step',
          maxLength: 4000,
          required: true,
        },
      ],
      editableData: _.clone(this.editorData),
      show: false,
      isDirty: false,
      fileService: WalkthroughAttachmentService,
    }
  },
  methods: {
    onSelectedStepDeleteClick(selectedItems) {
      this.onDeleteClick(selectedItems, 'dataTableId', this.editableData, 'steps', 'stepTable')
      // The way data is synchronized in wizer, forces use to wait two ticks to get up-to-date state
      this.$nextTick(() => {
        this.$nextTick(() => {
          this.$refs.stepTable.onRowUpdated()
        })
      })
    },
    isWholeRowNotEmpty(row) {
      // should short circuit the validation chain in case none of the cells in a row contains a
      // value
      const columnProperties = this.columns.flatMap((individualColumn) => {
        return individualColumn.property
      })
      const columnAttributes = _.pick(row, columnProperties)
      const columnValues = _.values(columnAttributes)
      return !!columnValues.filter((value) => !!value).length
    },
    async onTaskAddClick() {
      const refArray = ['numberField', 'nameField']
      this.editableData.steps = this.editableData.steps.filter((step) => {
        return this.isWholeRowNotEmpty(step)
      })
      this.$refs.stepTable.revalidate()
      if (this.$refs.stepTable.validationMessage) {
        const notification = {
          type: 'error',
          message: this.$refs.stepTable.validationMessage,
          duration: this.notificationDuration,
        }
        this.$store.dispatch('Notification/add', notification, { root: true })
        return
      }
      await this.onAddClick(refArray, this.taskValidator, 'taskModified')
    },
    addNewEmptyRow() {
      this.editableData.steps.push({
        number: '',
        step: '',
        dataTableId: _.uniqueId('datatable_'),
        rowProps: {
          isDirty: true,
        },
      })
    },
  },
  computed: {
    steps() {
      return this.editableData.steps || []
    },
    computedEditableData() {
      return { ...this.editableData }
    },
    ...mapState({
      notificationDuration: (state) => state.Notification.notificationDuration,
    }),
  },
  watch: {
    computedEditableData: {
      handler(newVal, oldVal) {
        if (!_.isEqual(JSON.stringify(newVal), JSON.stringify(oldVal))) {
          this.isDirty = true
        }
      },
      deep: true,
    },
  },
}
</script>

<style lang="postcss"></style>
